import { useCallback, useEffect, useState } from 'react';
import { fetchQueuePause, setQueuePause, PAUSABLE_JOBS } from '../api/queue';
import type { PausableJobMeta, QueuePause } from '../api/queue';
import {
  fetchAutomationSettings, setAutomationSetting,
  AUTO_APPROVE_SAFE_PRICES_KEY, AUTO_APPROVE_NEW_ITEMS_KEY, AUTO_APPROVE_HOME_VALUE_KEY,
} from '../api/automationSettings';
import type { AutomationSetting } from '../api/automationSettings';

interface ToggleMeta {
  key: string;
  title: string;
  description: string;
  /** Shown only when turning the setting ON, given the blast radius. */
  confirmMessage: string;
}

const TOGGLABLE_SETTINGS: ToggleMeta[] = [
  {
    key: AUTO_APPROVE_SAFE_PRICES_KEY,
    title: 'Auto-approve SAFE price increases',
    description:
      `When on, a SAFE price increase (within a vendor’s safe % threshold, no core-charge change, not discontinued) skips manual review and pushes straight to NetSuite — for any vendor whose own "Auto-approve safe" toggle (Vendors page) is also on. This is the global switch; both must be on for a vendor to auto-approve.`,
    confirmMessage:
      `Enable global auto-approve for SAFE price increases?\n\nEvery vendor whose own per-vendor toggle is already ON will immediately start auto-approving and pushing SAFE price changes to NetSuite — no manual review.`,
  },
  {
    key: AUTO_APPROVE_NEW_ITEMS_KEY,
    title: 'Auto-approve new items',
    description:
      `When on, newly detected items (not yet in NetSuite) skip manual review and go straight to the create queue — for any vendor whose own "Auto-add" toggle (Vendors page) is also on. This is the global switch; both must be on for a vendor’s new items to be auto-approved.`,
    confirmMessage:
      `Enable global auto-approve for new items?\n\nEvery vendor whose own "Auto-add" per-vendor toggle is already ON will immediately start auto-approving new items and queuing them for NetSuite create — no manual review.`,
  },
  {
    key: AUTO_APPROVE_HOME_VALUE_KEY,
    title: 'Auto-approve Home Value',
    description:
      `When on, a new Home Value proposal (the storefront lead-time label computed from the vendor's PDC stock) skips manual review and is pushed straight to NetSuite — for any vendor whose own "Auto-approve Home Value" toggle (Vendors page) is also on. This is the global switch; both must be on. Proposals already waiting in Review are not affected — approve them there. Pushes can be paused on the Job Queue.`,
    confirmMessage:
      `Enable global auto-approve for Home Value?\n\nEvery vendor whose own "Auto-approve Home Value" toggle is already ON will start auto-approving its new Home Value proposals on the next recompute and pushing them to NetSuite — no manual review.`,
  },
];

export default function AutomationPage() {
  const [rows, setRows]         = useState<QueuePause[]>([]);
  const [settings, setSettings] = useState<AutomationSetting[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [pauseRows, settingRows] = await Promise.all([
        fetchQueuePause(),
        fetchAutomationSettings(),
      ]);
      setRows(pauseRows);
      setSettings(settingRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="max-w-2xl pb-5">
      <h1 className="mb-5 text-xl font-semibold text-gray-900">Automation</h1>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white">
          <p className="px-5 py-8 text-center text-sm text-gray-400">Loading…</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="space-y-4">
            {TOGGLABLE_SETTINGS.map(meta => (
              <SettingCard
                key={meta.key}
                meta={meta}
                row={settings.find(s => s.key === meta.key) ?? null}
                onSaved={load}
              />
            ))}
          </div>

          <div className="space-y-4">
            {PAUSABLE_JOBS.map(meta => (
              <PauseCard
                key={meta.jobType}
                meta={meta}
                row={rows.find(r => r.job_type === meta.jobType) ?? null}
                onSaved={load}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingCard({
  meta,
  row,
  onSaved,
}: {
  meta: ToggleMeta;
  row: AutomationSetting | null;
  onSaved: () => Promise<void>;
}) {
  const [state, setState]   = useState<AutomationSetting | null>(row);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [reason, setReason] = useState(row?.reason ?? '');

  useEffect(() => {
    setState(row);
    setReason(row?.reason ?? '');
  }, [row]);

  async function toggle() {
    if (!state || saving) return;
    const next = !state.value;
    if (next && !window.confirm(meta.confirmMessage)) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await setAutomationSetting(meta.key, next, reason.trim() || null);
      setState(updated);
      setReason(updated.reason ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      await onSaved(); // revert to server truth on failure
    } finally {
      setSaving(false);
    }
  }

  const on = !!state?.value;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {!state ? (
        <p className="px-5 py-8 text-center text-sm text-gray-400">
          {meta.title} control is not configured.
        </p>
      ) : (
        <div className="p-5">
          {error && (
            <div className="mb-3 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
          )}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900">{meta.title}</h2>
              <p className="mt-1 text-sm text-gray-500">{meta.description}</p>
            </div>

            <BigToggle
              active={on}
              disabled={saving}
              onClick={toggle}
              label={on ? `Turn off: ${meta.title}` : `Turn on: ${meta.title}`}
            />
          </div>

          <div className="mt-4 flex items-center gap-2">
            <OnOffBadge on={on} />
            <span className="text-xs text-gray-400">
              {state.updated_by_email
                ? `Last changed by ${state.updated_by_email} · ${relTime(state.updated_at)}`
                : 'Never changed'}
            </span>
          </div>

          <label className="mt-4 block">
            <span className="text-xs font-medium text-gray-500">Reason (optional)</span>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. client signed off on Paccar/Cummins SAFE quality"
              rows={2}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
            />
            <span className="mt-1 block text-[11px] text-gray-400">
              Saved with the next toggle. Shown in the audit log.
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

function OnOffBadge({ on }: { on: boolean }) {
  return on ? (
    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
      On
    </span>
  ) : (
    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
      Off
    </span>
  );
}

function PauseCard({
  meta,
  row,
  onSaved,
}: {
  meta: PausableJobMeta;
  row: QueuePause | null;
  onSaved: () => Promise<void>;
}) {
  const [state, setState]   = useState<QueuePause | null>(row);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [reason, setReason] = useState(row?.reason ?? '');

  useEffect(() => {
    setState(row);
    setReason(row?.reason ?? '');
  }, [row]);

  async function toggle() {
    if (!state || saving) return;
    const next = !state.is_paused;
    if (next && !window.confirm(meta.confirmMessage)) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await setQueuePause(meta.jobType, next, reason.trim() || null);
      setState(updated);
      setReason(updated.reason ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      await onSaved(); // revert to server truth on failure
    } finally {
      setSaving(false);
    }
  }

  const paused = !!state?.is_paused;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {!state ? (
        <p className="px-5 py-8 text-center text-sm text-gray-400">
          {meta.title} control is not configured.
        </p>
      ) : (
        <div className="p-5">
          {error && (
            <div className="mb-3 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
          )}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900">{meta.title}</h2>
              <p className="mt-1 text-sm text-gray-500">
                Controls the{' '}
                <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{meta.jobType}</code>{' '}
                queue. {meta.description}
              </p>
            </div>

            <BigToggle
              active={!paused}
              disabled={saving}
              onClick={toggle}
              label={paused ? `Resume: ${meta.title}` : `Pause: ${meta.title}`}
            />
          </div>

          <div className="mt-4 flex items-center gap-2">
            <StatusBadge paused={paused} />
            <span className="text-xs text-gray-400">
              {state.updated_by_email
                ? `Last changed by ${state.updated_by_email} · ${relTime(state.updated_at)}`
                : 'Never changed'}
            </span>
          </div>

          <label className="mt-4 block">
            <span className="text-xs font-medium text-gray-500">Reason (optional)</span>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. vendor sent bad pricing — investigating"
              rows={2}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
            />
            <span className="mt-1 block text-[11px] text-gray-400">
              Saved with the next toggle. Shown in the global banner and the audit log.
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

function BigToggle({
  active,
  disabled,
  onClick,
  label,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
        active ? 'bg-green-500' : 'bg-amber-500'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
          active ? 'translate-x-[26px]' : 'translate-x-[4px]'
        }`}
      />
    </button>
  );
}

function StatusBadge({ paused }: { paused: boolean }) {
  return paused ? (
    <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
      Paused
    </span>
  ) : (
    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
      Active
    </span>
  );
}

function relTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return `${Math.round(diff)}s ago`;
  if (diff < 3600)  return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}
